import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Tag, Loader2, BookOpen, Newspaper } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { mockBlogPosts } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import CtaBanner from '../components/CtaBanner';
import IconBadge from '../components/IconBadge';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import { usePageMeta } from '../components/Seo';
import { useBrandName } from '../context/SettingsContext';
import EditableText from '../components/EditableText';
import { BACKEND_URL, DEFAULT_BRAND, BTN_PRIMARY, BTN_OUTLINE } from '../config/brand';

const mapApiPost = (p) => ({
  id: p.id,
  slug: p.slug || p.id,
  title: p.title,
  excerpt: p.excerpt || p.summary || '',
  content: p.content,
  author: p.author || 'Équipe',
  date: p.published_at || p.created_at,
  image: p.image_url || DEFAULT_BRAND.hero_image_url,
  category: p.category || 'Actualités',
});

const postPath = (post) => `/blog/${post.slug || post.id}`;

function formatDate(dateStr, options = { day: 'numeric', month: 'long', year: 'numeric' }) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('fr-FR', options);
}

function estimateReadingTime(text = '') {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

const Blog = () => {
  const brandName = useBrandName();
  const [posts, setPosts] = useState(mockBlogPosts);
  const [loading, setLoading] = useState(false);

  usePageMeta({
    title: `Blog ${brandName} — Actualités & Conseils Robotique`,
    description: `Actualités, conseils et ressources sur la robotique éducative par ${brandName}. Ateliers, stages et STEM pour enfants sur la Côte d'Azur.`,
    image: '/images/hero.jpg',
    path: '/blog',
  });

  useEffect(() => {
    if (!BACKEND_URL) return;
    const loadPosts = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${BACKEND_URL}/api/posts`);
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setPosts(data.map(mapApiPost));
        }
      } catch {
        // fallback mock
      } finally {
        setLoading(false);
      }
    };
    loadPosts();
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        eyebrow="Blog"
        eyebrowKey="blog.hero.eyebrow"
        title={`Actualités ${brandName}`}
        titleKey="blog.hero.title"
        subtitle="Actualités, conseils et ressources sur la robotique éducative pour parents et enseignants"
        subtitleKey="blog.hero.subtitle"
      />

      <CircuitDivider className="mb-0" />

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-600">
          <Loader2 className="h-10 w-10 text-[#3a00e0] animate-spin mb-4" />
          <p>Chargement des articles…</p>
        </div>
      ) : posts.length === 0 ? (
        <section className="py-16 sm:py-20 px-4">
          <div className="max-w-lg mx-auto text-center">
            <IconBadge icon={Newspaper} variant="gradient" size="lg" className="mx-auto mb-6" />
            <EditableText
              editKey="blog.empty.title"
              defaultValue="Aucun article pour le moment"
              as="h2"
              className="text-2xl font-bold text-slate-900 mb-3"
            />
            <EditableText
              editKey="blog.empty.text"
              defaultValue="Revenez bientôt pour découvrir nos actualités, conseils et retours d'expérience sur la robotique éducative."
              as="p"
              multiline
              className="text-slate-600 mb-8 leading-relaxed"
            />
            <Link to="/contact">
              <Button className={BTN_PRIMARY}>
                <EditableText editKey="blog.empty.cta" defaultValue="Nous contacter" as="span" />
              </Button>
            </Link>
          </div>
        </section>
      ) : (
        <>
          {featured && (
            <section className="py-12 sm:py-14 px-4">
              <div className="max-w-7xl mx-auto">
                <Reveal animation="fade-up">
                  <article className="grid lg:grid-cols-2 gap-0 lg:gap-10 items-stretch border border-slate-200 rounded-xl overflow-hidden bg-white">
                    <Link
                      to={postPath(featured)}
                      className="h-full min-h-[240px] sm:min-h-[280px] lg:min-h-[340px] overflow-hidden block"
                    >
                      <img
                        src={featured.image}
                        alt={featured.title}
                        loading="lazy"
                        className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500"
                      />
                    </Link>
                    <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
                      <Badge className="bg-[rgba(58,0,224,0.08)] text-[#3a00e0] border-[rgba(58,0,224,0.18)] w-fit mb-4">
                        <EditableText editKey="blog.featured.badge" defaultValue="Article en vedette" as="span" />
                      </Badge>
                      <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4 leading-tight tracking-tight">
                        <Link to={postPath(featured)} className="hover:text-[#3a00e0] transition-colors">
                          {featured.title}
                        </Link>
                      </h2>
                      <p className="text-slate-600 mb-6 leading-relaxed line-clamp-4">
                        {featured.excerpt}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500 mb-6">
                        {formatDate(featured.date) && (
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1.5 shrink-0" aria-hidden="true" />
                            <time dateTime={featured.date}>{formatDate(featured.date)}</time>
                          </div>
                        )}
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1.5 shrink-0" aria-hidden="true" />
                          <span>{featured.author}</span>
                        </div>
                        <div className="flex items-center">
                          <Tag className="h-4 w-4 mr-1.5 shrink-0" aria-hidden="true" />
                          <span>{featured.category}</span>
                        </div>
                        <div className="flex items-center">
                          <BookOpen className="h-4 w-4 mr-1.5 shrink-0" aria-hidden="true" />
                          <span>{estimateReadingTime(featured.excerpt)} min de lecture</span>
                        </div>
                      </div>
                      <Link to={postPath(featured)}>
                        <Button className={`${BTN_PRIMARY} w-full sm:w-fit`}>
                          <EditableText editKey="blog.featured.cta" defaultValue="Lire l'article" as="span" />
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </article>
                </Reveal>
              </div>
            </section>
          )}

          <section className="py-12 sm:py-14 px-4">
            <div className="max-w-7xl mx-auto">
              <SectionHeader
                eyebrow="Publications"
                eyebrowKey="blog.list.eyebrow"
                title="Derniers articles"
                titleKey="blog.list.title"
                align="left"
                mb="mb-8"
              />

              {rest.length === 0 ? (
                <EditableText
                  editKey="blog.list.empty"
                  defaultValue="Un seul article pour le moment — d'autres arrivent bientôt !"
                  as="p"
                  className="text-slate-600 text-center py-8 text-sm"
                />
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {rest.map((post, i) => (
                    <Reveal key={post.id} animation="fade-up" delay={i * 60}>
                      <article className="flex flex-col h-full group">
                        <Link to={postPath(post)} className="h-48 overflow-hidden rounded-xl block mb-4">
                          <img
                            src={post.image}
                            alt={post.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </Link>
                        <Badge
                          variant="outline"
                          className="border-[rgba(58,0,224,0.25)] text-[#3a00e0] w-fit mb-2"
                        >
                          {post.category}
                        </Badge>
                        <h3 className="text-slate-900 font-semibold text-lg mb-2 line-clamp-2 tracking-tight">
                          <Link to={postPath(post)} className="hover:text-[#3a00e0] transition-colors">
                            {post.title}
                          </Link>
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed line-clamp-3 mb-4 flex-1">
                          {post.excerpt}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-4">
                          {formatDate(post.date, { day: 'numeric', month: 'short', year: 'numeric' }) && (
                            <div className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1 shrink-0" aria-hidden="true" />
                              <time dateTime={post.date}>
                                {formatDate(post.date, {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </time>
                            </div>
                          )}
                          <div className="flex items-center">
                            <User className="h-3 w-3 mr-1 shrink-0" aria-hidden="true" />
                            <span>{post.author}</span>
                          </div>
                          <div className="flex items-center">
                            <BookOpen className="h-3 w-3 mr-1 shrink-0" aria-hidden="true" />
                            <span>{estimateReadingTime(post.excerpt)} min</span>
                          </div>
                        </div>
                        <Link to={postPath(post)}>
                          <Button variant="outline" className={`w-full ${BTN_OUTLINE}`}>
                            <EditableText editKey="blog.card.cta" defaultValue="Lire la suite" as="span" />
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </article>
                    </Reveal>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      <CtaBanner
        className="bg-slate-50"
        title="Restez informé"
        titleKey="blog.cta.title"
        subtitle="Inscrivez-vous à notre newsletter pour recevoir nos derniers articles et actualités sur la robotique éducative."
        subtitleKey="blog.cta.subtitle"
        footer="Pas de spam — désinscription possible à tout moment."
        footerKey="blog.cta.footer"
      >
        <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            type="email"
            placeholder="votre@email.com"
            aria-label="Adresse email pour la newsletter"
            className="flex-1 px-4 py-3 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder:text-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
          />
          <Button className="bg-white text-[#3a00e0] hover:bg-[rgba(58,0,224,0.06)] shrink-0 rounded-full">
            <EditableText editKey="blog.cta.button" defaultValue="S'abonner" as="span" />
          </Button>
        </div>
      </CtaBanner>

      <Footer />
    </div>
  );
};

export default Blog;
